import { Application, Container, Graphics, Texture, Ticker, ColorMatrixFilter } from 'pixi.js';
import type { ClimberPose, ColorType, MatchSide } from '@shared/types';
import type { EquippedSet } from '@shared/protocol';
import { HEADER_H } from '@shared/gameConfig';
import { Lane } from '../entities/Lane';
import { WrTrack } from '../entities/WrTrack';
import gsap from 'gsap';
import { spawnBurst } from '../fx/Burst';
import { spawnFloatText } from '../fx/FloatText';
import { spawnSunburst, spawnStarPop } from '../fx/WinFx';
import { FINISHERS, getFinisher, type FinisherCtx } from '../fx/finishers';
import { AdvancedBloomFilter } from 'pixi-filters/advanced-bloom';
import { loadClimberTextures, resetClimberTextureCache } from '../textures';
import { resetCosmeticTextureCache } from '../entities/Climber';
import { NEON_COLORS } from '../colors';
import type { MatchBackgroundTheme } from '../matchBackground';

export interface MatchSceneInit {
  canvasWidth: number;
  canvasHeight: number;
  yourSeq: ColorType[];
  rivalSeq: ColorType[];
  yourPath: number[];
  rivalPath: number[];
  yourColor: 'red' | 'blue';
  rivalColor: 'red' | 'blue';
  leftBackgroundTheme?: MatchBackgroundTheme;
  rightBackgroundTheme?: MatchBackgroundTheme;
}

export class MatchScene {
  readonly app: Application;
  private root: Container;
  private leftLane!: Lane;
  private rightLane!: Lane;
  private wrTrack!: WrTrack;
  private fxLayer!: Container;
  private resolvedTextures: Record<ClimberPose, Texture> | null = null;
  private tickerFn?: (t: Ticker) => void;

  constructor() {
    this.app = new Application();
    this.root = new Container();
  }

  async init(canvas: HTMLCanvasElement, opts: MatchSceneInit): Promise<void> {
    await this.app.init({
      canvas,
      width: opts.canvasWidth,
      height: opts.canvasHeight,
      background: 0x0e0b2e,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    this.app.stage.addChild(this.root);

    // Build immediately with a 1x1 placeholder texture so the lane is on-screen
    // within ~100ms. The real climber PNGs may take a couple of seconds over a
    // slow phone connection — we upgrade the sprites in the background instead
    // of blocking the whole scene init on the download.
    const placeholder = Texture.WHITE;
    this.resolvedTextures = {
      idle: placeholder,
      climb: placeholder,
      fall: placeholder,
      victory: placeholder,
    };
    this.buildLayout(opts);

    this.tickerFn = (t: Ticker) => {
      const dt = t.deltaMS / 1000;
      this.leftLane.update(dt);
      this.rightLane.update(dt);
    };
    this.app.ticker.add(this.tickerFn);

    // Async-load the real textures and hot-swap them in once they land.
    void loadClimberTextures()
      .then((real) => {
        if (this.app.renderer === null) return; // scene destroyed before load
        this.resolvedTextures = real;
        try {
          this.leftLane.setTextures(real);
          this.rightLane.setTextures(real);
        } catch (e) {
          console.warn('[scene] texture swap failed', e);
        }
      })
      .catch((e) => console.warn('[scene] texture load failed', e));
  }

  private buildLayout(opts: MatchSceneInit): void {
    const wrW = WrTrack.getWidth();
    const gapPx = 4;
    const laneW = Math.floor((opts.canvasWidth - wrW - gapPx * 2) / 2);

    const textures = this.resolvedTextures!;

    this.leftLane = new Lane({
      side: 'left',
      width: laneW,
      color: opts.yourColor,
      textures,
      seq: opts.yourSeq,
      path: opts.yourPath,
      backgroundTheme: opts.leftBackgroundTheme,
    });
    this.leftLane.x = 0;
    this.leftLane.y = HEADER_H;
    this.root.addChild(this.leftLane);

    this.wrTrack = new WrTrack();
    this.wrTrack.x = laneW + gapPx;
    this.wrTrack.y = HEADER_H;
    this.root.addChild(this.wrTrack);

    this.rightLane = new Lane({
      side: 'right',
      width: laneW,
      color: opts.rivalColor,
      textures,
      seq: opts.rivalSeq,
      path: opts.rivalPath,
      backgroundTheme: opts.rightBackgroundTheme,
    });
    this.rightLane.x = laneW + wrW + gapPx * 2;
    this.rightLane.y = HEADER_H;
    this.root.addChild(this.rightLane);

    // FX layer sits on top of everything in the canvas
    this.fxLayer = new Container();
    this.root.addChild(this.fxLayer);

    // Bloom on the FX layer makes neon sparks / confetti / sunburst / shockwave
    // glow. Applied ONLY to the (sparse, mostly-transparent) FX layer so it
    // never touches gameplay sprites — keeps the cost tiny. `threshold` drops
    // dim pixels so only bright FX bloom. Wrapped so a filter failure on an old
    // GPU degrades gracefully to no-glow instead of breaking the scene.
    try {
      this.fxLayer.filters = [
        new AdvancedBloomFilter({ threshold: 0.45, bloomScale: 1.2, brightness: 1, blur: 6, quality: 4 }),
      ];
    } catch (e) {
      console.warn('[scene] bloom filter unavailable', e);
    }

    // Draw lane header backgrounds (decorative; the actual avatar/name labels
    // live in the React HUD that overlays the canvas).
    const headers = new Graphics();
    headers
      .roundRect(0, 0, laneW, HEADER_H, 10)
      .fill({ color: 0x000000, alpha: 0.35 })
      .stroke({ color: NEON_COLORS[opts.yourColor].base, width: 1, alpha: 0.25 });
    headers
      .roundRect(laneW + wrW + gapPx * 2, 0, laneW, HEADER_H, 10)
      .fill({ color: 0x000000, alpha: 0.35 })
      .stroke({ color: NEON_COLORS[opts.rivalColor].base, width: 1, alpha: 0.25 });
    this.root.addChildAt(headers, 0);
  }

  setStep(side: MatchSide, step: number, force = false): void {
    if (side === 'left') this.leftLane.setStep(step, force);
    else this.rightLane.setStep(step, force);
  }

  setPose(side: MatchSide, pose: ClimberPose): void {
    if (!this.resolvedTextures) return;
    if (side === 'left') this.leftLane.setPose(pose, this.resolvedTextures);
    else this.rightLane.setPose(pose, this.resolvedTextures);
  }

  setShield(active: boolean): void {
    this.leftLane.setShield(active);
  }

  /** Trigger the finish-line bell on a given side. `sound` defaults to true
   * only for the local player's lane to avoid two beeps stacking. */
  triggerFinishBell(side: MatchSide, sound = true): void {
    if (side === 'left') this.leftLane.triggerFinishBell(sound);
    else this.rightLane.triggerFinishBell(sound);
  }

  /**
   * Full dramatic win sequence:
   *   1. Winner SLAMS the finish bell (existing buzzer) + star pop + sunburst.
   *   2. Winner does a victory STOMP — at the slam frame the loser is
   *      KNOCKED DOWN the wall (stomp-the-opponent), with dust, shockwave,
   *      confetti, and a hard screen shake.
   * The flashiest beat in the game — fires once on MATCH_OVER.
   */
  playWinSequence(winnerSide: MatchSide, stompPower = 1.0, fxKey?: string | null): void {
    const winnerLane = winnerSide === 'left' ? this.leftLane : this.rightLane;
    const loserLane = winnerSide === 'left' ? this.rightLane : this.leftLane;
    // Clamp so a misconfigured character can't shake the screen into oblivion.
    const p = Math.max(0.5, Math.min(2.5, stompPower));

    // 1. Bell on the winner's lane (with sound).
    winnerLane.triggerFinishBell(true);

    const wPos = winnerLane.getClimberWorldPos();
    const lPos = loserLane.getClimberWorldPos();

    // Sunburst + star behind the winner immediately (shared intro for all
    // finishers).
    spawnSunburst(this.fxLayer, wPos.x, wPos.y - 30);
    spawnStarPop(this.fxLayer, wPos.x, wPos.y - 70);

    // 2. Winner stomp → at impact: ALWAYS play the classic stomp finish (bell
    //    already rang above; here comes knockdown + shake + confetti), then
    //    LAYER the equipped Finisher's signature FX on top. The old effect is
    //    never removed — a special finisher only adds to it.
    const ctx: FinisherCtx = {
      fx: this.fxLayer,
      winner: wPos,
      loser: lPos,
      width: this.app.renderer.width,
      height: this.app.renderer.height,
      power: p,
      shake: (i, d) => this.screenShake(i, d),
      hitStop: (ms) => this.hitStop(ms),
      slowMo: (scale, ms) => this.slowMo(scale, ms),
      flash: (color, peak, dur) => this.flash(color, peak, dur),
      knockdownLoser: () => loserLane.playLoserKnockdown(),
    };
    winnerLane.playWinnerStomp(() => {
      FINISHERS.default!(ctx);
      const extra = getFinisher(fxKey);
      if (extra !== FINISHERS.default) extra(ctx);
    });
  }

  /** Partial slow-motion: scale GSAP + Pixi tick to `scale` for `ms` real-time,
   * then restore. Unlike hitStop (≈full freeze) this keeps motion readable. */
  private slowMo(scale = 0.3, ms = 600): void {
    try {
      gsap.globalTimeline.timeScale(scale);
      this.app.ticker.speed = scale;
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      gsap.globalTimeline.timeScale(1);
      try {
        if (this.app.renderer) this.app.ticker.speed = 1;
      } catch {
        /* renderer gone */
      }
    }, ms);
  }

  /** Full-screen colour flash on the stage (above the shaking root): ramp to
   * `peak` alpha then fade out over `durSec`. Self-destructs. */
  private flash(color: number, peak = 0.5, durSec = 0.4): void {
    try {
      const { width: W, height: H } = this.app.screen;
      const overlay = new Graphics();
      overlay.rect(0, 0, W, H).fill({ color });
      overlay.alpha = 0;
      this.app.stage.addChild(overlay);
      gsap
        .timeline({ onComplete: () => { if (!overlay.destroyed) overlay.destroy(); } })
        .to(overlay, { alpha: peak, duration: Math.min(0.1, durSec * 0.3), ease: 'power2.out' })
        .to(overlay, { alpha: 0, duration: durSec, ease: 'power2.in' });
    } catch {
      /* ignore */
    }
  }

  /** Briefly freeze the whole game (GSAP timelines + Pixi ticker) for `ms` to
   * punctuate an impact, then resume. The restore runs on real wall-clock time
   * so it always fires even though GSAP is frozen; GSAP's global timeScale is
   * restored unconditionally so a teardown mid-freeze can't lock the app. */
  private hitStop(ms = 90): void {
    try {
      gsap.globalTimeline.timeScale(0.0001);
      this.app.ticker.speed = 0.0001;
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      gsap.globalTimeline.timeScale(1);
      try {
        if (this.app.renderer) this.app.ticker.speed = 1;
      } catch {
        /* renderer gone */
      }
    }, ms);
  }

  /** Camera-style shake on the whole scene root. Decays over `duration`s. */
  private screenShake(intensity = 12, duration = 0.4): void {
    const root = this.root;
    const baseX = root.x;
    const baseY = root.y;
    const st = { t: 0 };
    gsap.to(st, {
      t: 1,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        if (root.destroyed) return;
        const damp = 1 - st.t;
        root.x = baseX + (Math.random() - 0.5) * intensity * 2 * damp;
        root.y = baseY + (Math.random() - 0.5) * intensity * 2 * damp;
      },
      onComplete: () => {
        if (root.destroyed) return;
        root.x = baseX;
        root.y = baseY;
      },
    });
  }

  /**
   * Screen-wide outcome flourish layered on top of the win/knockdown sequence:
   *   • WIN  → quick golden flash + a brightness pop that settles back to normal.
   *   • LOSE → red impact flash that settles into a desaturated, dimmed "defeat"
   *            veil, so a loss has real weight instead of just the rival's
   *            confetti. The drained colour reads as KO from the loser's seat.
   * Overlay lives on the stage (above the shaking root); the colour grade is a
   * ColorMatrixFilter on the game content. Both are torn down by destroy().
   */
  playOutcomeFx(youWon: boolean): void {
    try {
      const { width: W, height: H } = this.app.screen;
      const overlay = new Graphics();
      this.app.stage.addChild(overlay);
      const cm = new ColorMatrixFilter();
      this.root.filters = [cm];

      if (youWon) {
        overlay.rect(0, 0, W, H).fill({ color: 0xf4c724 });
        overlay.alpha = 0;
        gsap
          .timeline({ onComplete: () => { if (!overlay.destroyed) overlay.destroy(); } })
          .to(overlay, { alpha: 0.4, duration: 0.08, ease: 'power2.out' })
          .to(overlay, { alpha: 0, duration: 0.55, ease: 'power2.in' });
        const st = { b: 1 };
        gsap
          .timeline()
          .to(st, { b: 1.4, duration: 0.1, onUpdate: () => { if (!this.root.destroyed) cm.brightness(st.b, false); } })
          .to(st, {
            b: 1,
            duration: 0.6,
            onUpdate: () => { if (!this.root.destroyed) cm.brightness(st.b, false); },
            onComplete: () => { if (!this.root.destroyed) this.root.filters = []; },
          });
      } else {
        // Brief red impact flash that fully fades out — NO lingering veil.
        overlay.rect(0, 0, W, H).fill({ color: 0xe63946 });
        overlay.alpha = 0;
        gsap
          .timeline({ onComplete: () => { if (!overlay.destroyed) overlay.destroy(); } })
          .to(overlay, { alpha: 0.4, duration: 0.1, ease: 'power2.out' })
          .to(overlay, { alpha: 0, duration: 0.5, ease: 'power2.in' });
        // Momentary colour-drain that recovers — a "hit" pulse, not a held grade.
        const st = { sat: 0, bri: 1 };
        const apply = () => {
          if (this.root.destroyed) return;
          cm.saturate(st.sat, false);
          cm.brightness(st.bri, true);
        };
        gsap
          .timeline({ onComplete: () => { if (!this.root.destroyed) this.root.filters = []; } })
          .to(st, { sat: -0.6, bri: 0.85, duration: 0.12, ease: 'power2.out', onUpdate: apply })
          .to(st, { sat: 0, bri: 1, duration: 0.55, ease: 'power2.inOut', onUpdate: apply });
      }
    } catch (e) {
      console.warn('[scene] outcome fx failed', e);
    }
  }

  setVortex(active: boolean): void {
    this.rightLane.setVortex(active);
  }

  setRivalSeq(seq: ColorType[]): void {
    this.rightLane.updateSeq(seq);
  }

  /** Tell a lane which hold indices are about to be reshuffled by a Disrupt. */
  setDisruptingIndices(side: MatchSide, indices: number[]): void {
    const lane = side === 'left' ? this.leftLane : this.rightLane;
    lane.setDisruptingIndices(indices);
  }

  setYourSeq(seq: ColorType[]): void {
    this.leftLane.updateSeq(seq);
  }

  setEquipped(side: MatchSide, equipped: EquippedSet | null): void {
    const lane = side === 'left' ? this.leftLane : this.rightLane;
    lane.setEquipped(equipped);
  }

  /** Apply a Character skin (pose PNGs) to a lane's climber. */
  setCharacter(side: MatchSide, skin: import('@shared/protocol').CharacterSkin | null): void {
    const lane = side === 'left' ? this.leftLane : this.rightLane;
    lane.setCharacter(skin);
  }

  /** Apply a hold-shape skin. `side` targets one lane (2v2: each lane shows its
   * own team's skin); omit it to set BOTH lanes (1v1 local cosmetic). */
  setHoldShape(tex: import('pixi.js').Texture | null, side?: MatchSide): void {
    if (side === 'left') this.leftLane?.setHoldTexture(tex);
    else if (side === 'right') this.rightLane?.setHoldTexture(tex);
    else {
      this.leftLane?.setHoldTexture(tex);
      this.rightLane?.setHoldTexture(tex);
    }
  }

  setWrTime(t: number, over: boolean): void {
    this.wrTrack.setTime(t, over);
  }

  spawnTapBurst(side: MatchSide, color: ColorType, combo = 1): void {
    const lane = side === 'left' ? this.leftLane : this.rightLane;
    const anchor = lane.getFxAnchor();
    // Burst grows with the streak (capped) so chaining combos feels punchier.
    const scale = Math.min(1 + Math.max(0, combo - 1) * 0.05, 1.7);
    spawnBurst(this.fxLayer, lane.x + anchor.x, lane.y + anchor.y, color, scale);
    // A subtle, capped shake only kicks in on longer streaks — never per-tap
    // jitter that would make the screen feel noisy.
    if (combo >= 6) this.screenShake(Math.min((combo - 5) * 0.6, 4), 0.12);
  }

  spawnFloat(side: MatchSide, text: string, color: ColorType | 'cyan'): void {
    const lane = side === 'left' ? this.leftLane : this.rightLane;
    const anchor = lane.getFxAnchor();
    const hex = color === 'cyan' ? 0x22d3ee : NEON_COLORS[color].base;
    spawnFloatText(this.fxLayer, lane.x + anchor.x, lane.y + anchor.y - 28, text, hex);
  }

  destroy(): void {
    if (this.tickerFn) this.app.ticker.remove(this.tickerFn);
    this.leftLane?.destroyLane();
    this.rightLane?.destroyLane();
    // Pass renderer-options object (NOT a boolean) so we do NOT remove the
    // <canvas> from the DOM — React owns it via its ref and would otherwise
    // try to render into a detached node on the next mount.
    this.app.destroy({ removeView: false }, { children: true });
    // Drop the GPU-bound texture caches so the next scene gets fresh textures
    // tied to its new WebGL context. Without this, cached Texture references
    // outlive their renderer and sprites render blank on rematch.
    resetClimberTextureCache();
    resetCosmeticTextureCache();
  }
}
